import { cn, DatePicker, Text } from '@viapeople/components'
import { useState } from 'react'
import { FormLabelWithTooltip } from './FormLabelWithTooltip'
import type { ComponentProps, DatePickerCustomProps } from './types'

type FormsDatePickerProps = ComponentProps & {
	value?: string
	onChange?: (value: string) => void
	readOnly?: boolean
	disabled?: boolean
	hasError?: boolean
}

function parseUTCStamp(timestamp: string): Date {
	const date = new Date(timestamp)
	return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

export function FormsDatePicker({
	component,
	value,
	onChange,
	readOnly,
	disabled,
	required,
	hasError,
}: FormsDatePickerProps) {
	const { label, labelColor, labelSize, labelWeight, allowPastDates, maximumDate, tooltipEnabled, tooltipText } =
		component.custom as DatePickerCustomProps
	const [internalValue, setInternalValue] = useState('')

	const handleChange = (date: string) => {
		if (onChange) {
			onChange(date)
		} else {
			setInternalValue(date)
		}
	}

	const formatDisplayDate = (isoDate: string): string => {
		if (!isoDate) return ''
		const date = parseUTCStamp(isoDate)
		return date.toLocaleDateString('en-us', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		})
	}

	return (
		<div className='flex flex-col gap-1 w-full'>
			{label && !readOnly && (
				<FormLabelWithTooltip
					label={label}
					labelColor={labelColor}
					labelSize={labelSize}
					labelWeight={labelWeight}
					required={required}
					readOnly={readOnly}
					tooltipEnabled={tooltipEnabled}
					tooltipText={tooltipText}
				/>
			)}
			<div className='flex items-center gap-1'>
				{readOnly ? (
					<div className='bg-neutral-50 border border-neutral-200 rounded-md px-6 py-4'>
						<Text>{value ? formatDisplayDate(value) : ''}</Text>
					</div>
				) : (
					<div className={cn(hasError && 'border-2 border-red-500 rounded', 'w-full max-w-xs')}>
						<DatePicker
							value={value !== undefined ? value : internalValue}
							onChange={handleChange}
							placeholder='Select date'
							allowPastDates={allowPastDates}
							max={maximumDate || undefined}
							disabled={disabled}
						/>
					</div>
				)}
			</div>
		</div>
	)
}
